import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Check, X, Calendar, Clock, AlertCircle, Loader2, ArrowLeft, Phone, Mail, RefreshCw, CalendarDays } from 'lucide-react';
import Navbar from '../components/common/Navbar';
import Footer from '../components/common/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

export const ConfermaAppuntamento = () => {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showRescheduleOptions, setShowRescheduleOptions] = useState(false);
  const [appointmentInfo, setAppointmentInfo] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // Scroll to top
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Load appointment info on mount
  useEffect(() => {
    loadAppointmentInfo();
  }, [token]);

  const loadAppointmentInfo = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API}/api/appointments/info-from-token/${token}`);
      setAppointmentInfo(response.data);
      
      // Se già confermato o cancellato, mostra messaggio
      if (response.data.patient_confirmed) {
        setResult({
          type: 'already_confirmed',
          message: 'Questo appuntamento è già stato confermato!',
          date: response.data.appointment_date,
          time: response.data.appointment_time
        });
      } else if (response.data.is_cancelled) {
        setError('Questo appuntamento è stato cancellato.');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Link non valido o scaduto');
    } finally {
      setLoadingInfo(false);
    }
  };

  // Ref per tracciare se la conferma è già stata inviata (evita chiamate duplicate)
  const confirmSentRef = React.useRef(false);

  // Check if action was passed in URL (for direct links from SMS)
  useEffect(() => {
    const action = searchParams.get('action');
    if (action === 'confirm' && appointmentInfo && !appointmentInfo.patient_confirmed && !confirmSentRef.current && !loading) {
      confirmSentRef.current = true;
      handleConfirm();
    }
    // Don't auto-decline, show options instead
  }, [searchParams, appointmentInfo]);

  const handleConfirm = async () => {
    // Protezione contro double-click
    if (loading || confirmSentRef.current === 'done') return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/api/appointments/confirm-link/${token}?action=confirm`);
      confirmSentRef.current = 'done';
      setResult({
        type: 'success',
        message: response.data.message,
        status: response.data.status,
        date: response.data.appointment_date || appointmentInfo?.appointment_date,
        time: response.data.appointment_time || appointmentInfo?.appointment_time
      });
    } catch (err) {
      confirmSentRef.current = false; // Reset on error to allow retry
      setError(err.response?.data?.detail || 'Si è verificato un errore. Riprova o contattaci.');
    } finally {
      setLoading(false);
    }
  };

  const handleDecline = async () => {
    // Invece di declinare subito, mostra le opzioni di rischedulazione
    setShowRescheduleOptions(true);
    
    // Carica slot disponibili per lo stesso giorno
    if (appointmentInfo?.appointment_date) {
      await loadAvailableSlotsForDay(appointmentInfo.appointment_date);
    }
  };

  const loadAvailableSlotsForDay = async (date) => {
    setLoadingSlots(true);
    try {
      // Prova a caricare slot disponibili per lo stesso giorno
      const response = await axios.get(`${API}/api/availability/date/${date}`);
      const slots = response.data.slots || response.data || [];
      
      // Filtra slot già passati e lo slot corrente
      const currentTime = appointmentInfo?.appointment_time;
      const filteredSlots = slots.filter(slot => {
        const slotTime = slot.time || slot;
        return slotTime !== currentTime;
      });
      
      setAvailableSlots(filteredSlots);
    } catch (err) {
      console.error('Errore caricamento slot:', err);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleChangeTimeSlot = async (newTime) => {
    setLoading(true);
    setError(null);
    
    try {
      // Aggiorna l'appuntamento con il nuovo orario
      const response = await axios.post(`${API}/api/appointments/reschedule-from-token/${token}`, {
        new_time: newTime,
        same_day: true
      });
      
      setResult({
        type: 'success',
        message: 'Appuntamento riprogrammato con successo!',
        date: appointmentInfo.appointment_date,
        time: newTime
      });
      setShowRescheduleOptions(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Errore nella modifica. Riprova o contattaci.');
    } finally {
      setLoading(false);
    }
  };

  const handleChangeDayClick = () => {
    // Marca come "da riprogrammare" e vai al calendario
    axios.get(`${API}/api/appointments/confirm-link/${token}?action=decline`).then(() => {
      // Naviga al calendario per prenotare
      navigate('/prenota', { 
        state: { 
          reschedule: true,
          originalDate: appointmentInfo?.appointment_date,
          doctorName: appointmentInfo?.doctor_name
        }
      });
    }).catch(() => {
      navigate('/prenota');
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('it-IT', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-sky-50 via-white to-sky-50">
      <Navbar />
      
      <main className="flex-1 py-8 px-4">
        <div className="max-w-md mx-auto">
          {/* Logo */}
          <div className="text-center mb-6">
            <h1 className="text-2xl font-light tracking-widest" style={{fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#2A9FCB'}}>
              PRIME VISION
            </h1>
            <p className="text-xs text-gray-500 mt-1">Centro Oculistico d'Eccellenza</p>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-sky-100">
            {/* Loading Info */}
            {loadingInfo && (
              <div className="p-8 text-center">
                <Loader2 className="w-12 h-12 mx-auto animate-spin mb-4" style={{color: '#2A9FCB'}} />
                <p className="text-gray-600">Caricamento dettagli appuntamento...</p>
              </div>
            )}

            {/* Error */}
            {error && !loading && (
              <div className="p-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{background: '#FEE2E2'}}>
                  <AlertCircle className="w-8 h-8" style={{color: '#DC2626'}} />
                </div>
                <h2 className="text-xl font-bold text-center mb-2" style={{color: '#DC2626'}}>Ops!</h2>
                <p className="text-center text-gray-600 mb-6">{error}</p>
                <button
                  onClick={() => navigate('/')}
                  className="w-full py-3 rounded-lg font-medium"
                  style={{background: '#2A9FCB', color: 'white'}}
                >
                  Torna alla Home
                </button>
              </div>
            )}

            {/* Already Confirmed */}
            {result?.type === 'already_confirmed' && !loading && (
              <div className="p-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{background: '#D1FAE5'}}>
                  <Check className="w-8 h-8" style={{color: '#059669'}} />
                </div>
                <h2 className="text-xl font-bold text-center mb-2" style={{color: '#059669'}}>
                  Già Confermato!
                </h2>
                <p className="text-center text-gray-600 mb-4">{result.message}</p>
                
                {(result.date || result.time) && (
                  <div className="bg-gray-50 rounded-xl p-4 mb-6">
                    {result.date && (
                      <div className="flex items-center gap-3 mb-2">
                        <Calendar className="w-5 h-5" style={{color: '#2A9FCB'}} />
                        <span className="font-medium">{formatDate(result.date)}</span>
                      </div>
                    )}
                    {result.time && (
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5" style={{color: '#2A9FCB'}} />
                        <span className="font-medium text-lg">Ore {result.time}</span>
                      </div>
                    )}
                  </div>
                )}
                <button
                  onClick={() => navigate('/')}
                  className="w-full py-3 rounded-lg font-medium"
                  style={{background: '#2A9FCB', color: 'white'}}
                >
                  Torna alla Home
                </button>
              </div>
            )}

            {/* Success - Confirmed */}
            {result?.type === 'success' && !loading && (
              <div className="p-8">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center" style={{background: '#D1FAE5'}}>
                  <Check className="w-10 h-10" style={{color: '#059669'}} />
                </div>
                <h2 className="text-2xl font-bold text-center mb-2" style={{color: '#059669'}}>
                  Appuntamento Confermato!
                </h2>
                <p className="text-center text-gray-600 mb-6">{result.message}</p>
                
                {(result.date || result.time) && (
                  <div className="bg-gradient-to-r from-sky-50 to-sky-50 rounded-xl p-5 mb-6 border border-sky-100">
                    {result.date && (
                      <div className="flex items-center gap-3 mb-3">
                        <Calendar className="w-6 h-6" style={{color: '#2A9FCB'}} />
                        <span className="font-semibold text-lg">{formatDate(result.date)}</span>
                      </div>
                    )}
                    {result.time && (
                      <div className="flex items-center gap-3">
                        <Clock className="w-6 h-6" style={{color: '#2A9FCB'}} />
                        <span className="font-bold text-2xl" style={{color: '#2A9FCB'}}>Ore {result.time}</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="text-center space-y-3">
                  <button
                    onClick={() => navigate('/dashboard/patient')}
                    className="w-full py-3 rounded-lg font-semibold text-white"
                    style={{background: '#2A9FCB'}}
                  >
                    Vai alla tua Area Personale
                  </button>
                  <button
                    onClick={() => navigate('/')}
                    className="w-full py-3 rounded-lg font-medium border-2"
                    style={{borderColor: '#2A9FCB', color: '#2A9FCB'}}
                  >
                    Torna alla Home
                  </button>
                </div>
              </div>
            )}

            {/* Reschedule Options */}
            {showRescheduleOptions && !result && !error && !loading && (
              <div className="p-8">
                <h2 className="text-xl font-bold text-center mb-2" style={{color: '#2A9FCB'}}>
                  Come vuoi modificare?
                </h2>
                <p className="text-center text-gray-600 mb-6">
                  Scegli se cambiare solo l'orario o prenotare un altro giorno
                </p>

                {/* Current appointment info */}
                <div className="bg-gray-50 rounded-xl p-4 mb-6">
                  <p className="text-sm text-gray-500 mb-2">Appuntamento attuale:</p>
                  <div className="flex items-center gap-2 mb-1">
                    <Calendar className="w-4 h-4" style={{color: '#2A9FCB'}} />
                    <span className="font-medium">{formatDate(appointmentInfo?.appointment_date)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" style={{color: '#2A9FCB'}} />
                    <span className="font-semibold" style={{color: '#2A9FCB'}}>Ore {appointmentInfo?.appointment_time}</span>
                  </div>
                </div>

                {/* Option 1: Change time same day */}
                <div className="mb-4">
                  <button
                    onClick={() => loadAvailableSlotsForDay(appointmentInfo?.appointment_date)}
                    className="w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-3"
                    style={{background: 'linear-gradient(135deg, #2A9FCB, #2A9FCB)'}}
                  >
                    <RefreshCw className="w-5 h-5" />
                    Cambia Orario (stesso giorno)
                  </button>

                  {/* Available slots for same day */}
                  {loadingSlots && (
                    <div className="mt-4 text-center py-4">
                      <Loader2 className="w-6 h-6 mx-auto animate-spin" style={{color: '#2A9FCB'}} />
                      <p className="text-sm text-gray-500 mt-2">Caricamento orari disponibili...</p>
                    </div>
                  )}

                  {!loadingSlots && availableSlots.length > 0 && (
                    <div className="mt-4 bg-sky-50 rounded-xl p-4 border border-sky-100">
                      <p className="text-sm font-medium mb-3" style={{color: '#2A9FCB'}}>
                        Orari disponibili per {formatDate(appointmentInfo?.appointment_date)}:
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        {availableSlots.map((slot, idx) => {
                          const time = typeof slot === 'string' ? slot : slot.time;
                          return (
                            <button
                              key={idx}
                              onClick={() => handleChangeTimeSlot(time)}
                              className={`py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                                selectedSlot === time 
                                  ? 'bg-sky-600 text-white' 
                                  : 'bg-white text-sky-700 hover:bg-sky-100 border border-sky-200'
                              }`}
                            >
                              {time}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {!loadingSlots && availableSlots.length === 0 && (
                    <div className="mt-4 bg-amber-50 rounded-xl p-4 border border-amber-200">
                      <p className="text-sm text-amber-800">
                        Nessun altro orario disponibile per questo giorno. Scegli un altro giorno.
                      </p>
                    </div>
                  )}
                </div>

                {/* Option 2: Change day */}
                <button
                  onClick={handleChangeDayClick}
                  className="w-full py-4 rounded-xl font-semibold border-2 flex items-center justify-center gap-3 mb-4"
                  style={{borderColor: '#2A9FCB', color: '#2A9FCB'}}
                >
                  <CalendarDays className="w-5 h-5" />
                  Cambia Giorno (vai al calendario)
                </button>

                {/* Cancel - go back */}
                <button
                  onClick={() => setShowRescheduleOptions(false)}
                  className="w-full py-3 rounded-lg text-gray-500 hover:text-gray-700 text-sm flex items-center justify-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Annulla, torna indietro
                </button>
              </div>
            )}

            {/* Initial State - No action taken yet */}
            {!result && !error && !loading && !loadingInfo && appointmentInfo && !showRescheduleOptions && (
              <div className="p-8">
                <h2 className="text-xl font-bold text-center mb-2" style={{color: '#2A9FCB'}}>
                  Conferma il tuo Appuntamento
                </h2>
                <p className="text-center text-gray-600 mb-6">
                  Verifica i dettagli e conferma o richiedi una modifica
                </p>

                {/* Appointment Details */}
                <div className="bg-gradient-to-r from-sky-50 to-sky-50 rounded-xl p-5 mb-6 border border-sky-100">
                  <h3 className="font-semibold mb-4 text-center" style={{color: '#2A9FCB'}}>
                    📋 Dettagli Appuntamento
                  </h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                      <Calendar className="w-5 h-5 flex-shrink-0" style={{color: '#2A9FCB'}} />
                      <div>
                        <p className="text-xs text-gray-500">Data</p>
                        <p className="font-semibold" style={{color: '#2A9FCB'}}>
                          {formatDate(appointmentInfo.appointment_date)}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                      <Clock className="w-5 h-5 flex-shrink-0" style={{color: '#2A9FCB'}} />
                      <div>
                        <p className="text-xs text-gray-500">Orario</p>
                        <p className="font-bold text-xl" style={{color: '#2A9FCB'}}>
                          {appointmentInfo.appointment_time || 'Da definire'}
                        </p>
                      </div>
                    </div>
                    
                    {appointmentInfo.doctor_name && (
                      <div className="flex items-center gap-3 bg-white p-3 rounded-lg">
                        <svg className="w-5 h-5 flex-shrink-0" style={{color: '#2A9FCB'}} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <div>
                          <p className="text-xs text-gray-500">Medico</p>
                          <p className="font-semibold" style={{color: '#2A9FCB'}}>
                            {appointmentInfo.doctor_name}
                          </p>
                        </div>
                      </div>
                    )}

                    {appointmentInfo.services && appointmentInfo.services.length > 0 && (
                      <div className="bg-white p-3 rounded-lg">
                        <p className="text-xs text-gray-500 mb-2">Prestazioni</p>
                        <div className="flex flex-wrap gap-1">
                          {appointmentInfo.services.slice(0, 3).map((service, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 rounded-full" style={{background: '#E0F2FE', color: '#0369A1'}}>
                              {service}
                            </span>
                          ))}
                          {appointmentInfo.services.length > 3 && (
                            <span className="text-xs px-2 py-1 rounded-full bg-gray-100 text-gray-600">
                              +{appointmentInfo.services.length - 3} altre
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-4">
                  <button
                    onClick={handleConfirm}
                    disabled={loading}
                    className="w-full py-4 rounded-xl font-bold text-white flex items-center justify-center gap-3 transition-all hover:opacity-90"
                    style={{background: '#059669'}}
                  >
                    {loading ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <>
                        <Check className="w-6 h-6" />
                        ✅ SÌ, CONFERMO
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={handleDecline}
                    disabled={loading}
                    className="w-full py-4 rounded-xl font-bold text-white flex items-center justify-center gap-3 transition-all hover:opacity-90"
                    style={{background: '#DC2626'}}
                  >
                    <X className="w-6 h-6" />
                    ❌ NO, DEVO MODIFICARE
                  </button>
                </div>

                <p className="text-xs text-center text-gray-400 mt-6">
                  Cliccando su "Devo modificare" potrai cambiare orario o giorno
                </p>
              </div>
            )}

            {/* Loading overlay */}
            {loading && (
              <div className="absolute inset-0 bg-white/80 flex items-center justify-center">
                <Loader2 className="w-10 h-10 animate-spin" style={{color: '#2A9FCB'}} />
              </div>
            )}
          </div>

          {/* Contact Info */}
          <div className="mt-6 text-center text-sm text-gray-500">
            <p>Hai bisogno di assistenza?</p>
            <div className="flex justify-center gap-4 mt-2">
              <a href="tel:+390801234567" className="flex items-center gap-1 hover:text-sky-600">
                <Phone className="w-4 h-4" />
                Chiama
              </a>
              <a href="mailto:info@primevision.it" className="flex items-center gap-1 hover:text-sky-600">
                <Mail className="w-4 h-4" />
                Email
              </a>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ConfermaAppuntamento;
